const PENDING_STATUSES = new Set([
  'detected',
  'dry_run',
  'send_queued',
  'sending',
  'send_interrupted_no_replay',
  'midas_price_update_processing',
  'midas_laudo_registering',
]);

const SENT_STATUSES = new Set([
  'sent',
  'sent_registered_match',
  'partial_sent_failed',
  'midas_price_updated',
  'midas_price_auto_reduced',
  'midas_price_manual_reduced',
  'midas_laudo_registered',
  'midas_laudo_auto_registered',
  'midas_laudo_manual_registered',
  'reactivation_realized',
]);

const FAILED_STATUSES = new Set([
  'failed',
  'partial_sent_failed',
  'midas_price_update_failed',
  'midas_laudo_register_failed',
]);

const BLOCKED_STATUSES = new Set([
  'inactive_in_competitor',
  'owner_not_authorized',
  'reduction_not_authorized',
  'skipped_current_price',
  'skipped_duplicate',
  'skipped_invalid_address',
  'skipped_invalid_complement',
  'skipped_out_of_city',
  'skipped_out_of_coverage',
  'skipped_recently_sent',
  'skipped_registered_match',
  'skipped_rent_only',
  'sold',
]);

const STALE_AFTER_MS = 6 * 60 * 60 * 1000;

const numberFormatter = new Intl.NumberFormat('pt-BR');

const nonEmpty = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

const parseDate = value => {
  if (!nonEmpty(value)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const maxDate = values =>
  values
    .map(parseDate)
    .filter(Boolean)
    .sort((a, b) => b.getTime() - a.getTime())[0] || null;

const isFailedStatus = status => FAILED_STATUSES.has(String(status || ''));
const isBlockedStatus = status => {
  const value = String(status || '');
  return BLOCKED_STATUSES.has(value) || value.startsWith('skipped_');
};
const isPendingStatus = status => PENDING_STATUSES.has(String(status || ''));
const isSentStatus = status => SENT_STATUSES.has(String(status || ''));

const toneForStatus = status => {
  if (isFailedStatus(status)) return 'danger';
  if (isBlockedStatus(status)) return 'warning';
  if (isPendingStatus(status)) return 'info';
  if (isSentStatus(status)) return 'positive';
  return 'neutral';
};

const labelForStatus = item =>
  item?.statusLabel || item?.status || 'Status desconhecido';

const operationReasonForItem = item => {
  const status = String(item?.status || '');
  if (status === 'skipped_invalid_complement') {
    return 'Complemento ausente ou invalido bloqueou a fila comercial.';
  }
  if (status === 'skipped_out_of_coverage') {
    return 'Bairro ou cidade fora da cobertura operacional configurada.';
  }
  if (status === 'inactive_in_competitor') {
    return 'Fonte concorrente indica anuncio inativo; acao comercial fica bloqueada.';
  }
  if (status === 'midas_price_update_processing') {
    return 'Atualizacao de preco ja foi enfileirada para o Midas.';
  }
  if (status === 'midas_laudo_registering') {
    return 'Cadastro de laudo ja foi enfileirado para o Midas.';
  }
  if (isFailedStatus(status)) {
    return item?.error || 'Falha registrada no processamento ou provider.';
  }
  if (isBlockedStatus(status)) {
    return item?.error || 'Item bloqueado ou ignorado por regra operacional.';
  }
  if (isSentStatus(status)) {
    return 'Mensagem ou acao ja foi registrada no fluxo operacional.';
  }
  return item?.error || 'Classificacao derivada do status atual do pendente.';
};

const propertyTitle = item =>
  [item?.address, item?.streetNumber, item?.complement]
    .filter(nonEmpty)
    .join(', ') ||
  item?.matchedPropertyCode ||
  item?.sourceListingId ||
  `Pendente #${item?.id || '-'}`;

const sourceNameForItem = item =>
  item?.sourcePortal ||
  item?.sourceKind ||
  item?.sourceListingId ||
  'Fonte nao identificada';

const sensitiveMessageField = field => {
  const haystack = [field?.key, field?.label, field?.name]
    .filter(nonEmpty)
    .join(' ')
    .toLowerCase();
  return /cpf|cnpj|document|email|owner|phone|propriet|tel/.test(haystack);
};

const redactedMessageFields = fields => {
  if (Array.isArray(fields)) {
    return fields.map(field => ({
      ...field,
      value: sensitiveMessageField(field) ? '[redigido]' : field.value,
    }));
  }
  if (fields && typeof fields === 'object') {
    return Object.entries(fields).reduce((payload, [key, value]) => {
      payload[key] = sensitiveMessageField({ key }) ? '[redigido]' : value;
      return payload;
    }, {});
  }
  return fields;
};

const countStatuses = (kinds, predicate) =>
  kinds
    .flatMap(kind => kind?.statuses || [])
    .filter(row => predicate(row?.status))
    .reduce((sum, row) => sum + Number(row?.count || 0), 0);

const formatted = value => numberFormatter.format(Number(value || 0));

const summarizeSources = items => {
  const grouped = new Map();
  items.forEach(item => {
    const name = sourceNameForItem(item);
    const current = grouped.get(name) || {
      name,
      status: 'healthy',
      lastRunAt: '',
      successCount: 0,
      failureCount: 0,
      coverage: { value: 0, total: 0 },
      icon: 'i-lucide-rss',
      canSync: false,
    };
    current.coverage.total += 1;
    if (isFailedStatus(item?.status) || item?.error) {
      current.failureCount += 1;
    } else {
      current.successCount += 1;
      current.coverage.value += 1;
    }
    const latest = maxDate([
      current.lastRunAt,
      item?.processedAt,
      item?.sentAt,
    ]);
    current.lastRunAt = latest ? latest.toISOString() : current.lastRunAt;
    current.status =
      current.failureCount > current.successCount ? 'error' : 'healthy';
    grouped.set(name, current);
  });
  return Array.from(grouped.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

const buildTimeline = items =>
  items
    .slice()
    .sort((a, b) => {
      const dateA = parseDate(a?.sentAt || a?.processedAt)?.getTime() || 0;
      const dateB = parseDate(b?.sentAt || b?.processedAt)?.getTime() || 0;
      return dateB - dateA;
    })
    .slice(0, 8)
    .map(item => ({
      id: item?.id,
      at: item?.sentAt || item?.processedAt,
      actor: sourceNameForItem(item),
      title: `#${item?.id || '-'} - ${labelForStatus(item)}`,
      description: `${propertyTitle(item)}. ${operationReasonForItem(item)}`,
      tone: toneForStatus(item?.status),
      redacted: false,
    }));

const buildExpectedLocks = activeKind => {
  const kind = activeKind || 'all';
  return [
    {
      id: 'import-hooks',
      label: 'Import hooks',
      key: 'lais:pending_market:import_hooks:<account>:<hook>',
      status: 'Sem endpoint live',
      tone: 'neutral',
      helper: 'Importar Anuncios, QuintoAndar, Zap e fontes correlatas.',
    },
    {
      id: 'group-send',
      label: 'Envio para grupos',
      key: `lais:pending_market:group_send:<account>:${kind}`,
      status: 'Esperado',
      tone: 'info',
      helper: 'Serializa mensagem por tipo antes do envio no WhatsApp.',
    },
    {
      id: 'midas-sync',
      label: 'MIDAS status sync',
      key: 'lais:pending_market:midas_status_sync:<account>',
      status: 'Esperado',
      tone: 'info',
      helper: 'Concilia logs do Midas com status de pendentes.',
    },
    {
      id: 'detection',
      label: 'Deteccao',
      key: 'lais:pending_market:detection:<account>:*',
      status: 'Esperado',
      tone: 'info',
      helper: 'Classifica fora da casa, reducao, reativacao e bloqueios.',
    },
  ];
};

export const pendingMarketOperationPayloadForItem = item => {
  if (!item) return null;

  return {
    item: {
      id: item.id,
      kind: item.kind,
      status: item.status,
      statusLabel: item.statusLabel,
      groupedItemIds: item.groupedItemIds,
      groupedItemCount: item.groupedItemCount,
      processedAt: item.processedAt,
      sentAt: item.sentAt,
      error: item.error,
    },
    rule: {
      reason: operationReasonForItem(item),
      tone: toneForStatus(item.status),
      visibleInQueue: !isBlockedStatus(item.status),
    },
    source: {
      kind: item.sourceKind,
      portal: item.sourcePortal,
      listingId: item.sourceListingId,
      url: item.sourceUrl,
      relatedSourceListings: item.relatedSourceListings,
    },
    midas: {
      matchedPropertyCode: item.matchedPropertyCode,
      matchedPropertyRef: item.matchedPropertyRef,
      houseStatus: item.houseStatus,
      houseIsActive: item.houseIsActive,
      competitorPrice: item.competitorPrice,
      housePrice: item.housePrice,
      optionistName: item.optionistName,
      optionistUserId: item.optionistUserId,
    },
    delivery: {
      whatsappConversaId: item.whatsappConversaId,
      messageHeader: item.messageHeader,
      messageFields: redactedMessageFields(item.messageFields),
      messageText: item.messageText ? '[redigido]' : '',
    },
    enrichment: {
      cnefeAddressId: item.cnefeAddressId,
      transactionHistory: item.transactionHistory,
      ownerEnrichment: item.ownerEnrichment,
    },
  };
};

export const normalizePendingMarketOperationSnapshot = ({
  kinds = [],
  items = [],
  activeKind = '',
  pageMeta = {},
  source = '',
  errorMessage = '',
  loading = false,
  operationSnapshot = null,
  now = new Date(),
} = {}) => {
  const activeKindRow =
    kinds.find(kind => String(kind?.kind) === String(activeKind)) || {};
  const latestProcessedAt = maxDate([
    activeKindRow.lastProcessedAt,
    ...kinds.map(kind => kind?.lastProcessedAt),
    ...items.map(item => item?.processedAt),
  ]);
  const latestSentAt = maxDate([
    activeKindRow.lastSentAt,
    ...kinds.map(kind => kind?.lastSentAt),
    ...items.map(item => item?.sentAt),
  ]);
  const nowDate = parseDate(now) || new Date();
  const stale =
    Boolean(latestProcessedAt) &&
    nowDate.getTime() - latestProcessedAt.getTime() > STALE_AFTER_MS;
  const failedCount = Math.max(
    countStatuses(kinds, isFailedStatus),
    items.filter(item => isFailedStatus(item?.status) || item?.error).length
  );
  const blockedCount = countStatuses(kinds, isBlockedStatus);
  const pageTotal = Number(pageMeta?.total || activeKindRow.total || 0);

  let runStatus = 'completed';
  if (loading) runStatus = 'running';
  else if (errorMessage) runStatus = 'failed';
  else if (stale) runStatus = 'stale';
  else if (!latestProcessedAt) runStatus = 'queued';

  const fallback = {
    runPanel: {
      status: runStatus,
      startedAt: latestProcessedAt ? latestProcessedAt.toISOString() : '',
      finishedAt: latestSentAt ? latestSentAt.toISOString() : '',
      worker: 'pending_market_updates',
      lock: {
        kind: activeKind || 'all',
        source: source || 'unknown',
        page: pageMeta?.page || 1,
      },
      error: errorMessage || '',
      stale,
      progress: loading ? 45 : null,
      nextSteps: errorMessage
        ? ['Recarregue a aba Operacao.', 'Confira workers e endpoint upstream.']
        : [],
    },
    metrics: [
      {
        id: 'total',
        label: 'Registros no filtro',
        value: formatted(pageTotal),
        helper: activeKindRow.label || 'Tipo ativo',
        tone: 'neutral',
        icon: 'i-lucide-list-checks',
      },
      {
        id: 'pending',
        label: 'Pendentes',
        value: formatted(countStatuses(kinds, isPendingStatus)),
        helper: 'Aguardam acao ou envio',
        tone: 'warning',
        icon: 'i-lucide-clock-3',
      },
      {
        id: 'sent',
        label: 'Enviados',
        value: formatted(countStatuses(kinds, isSentStatus)),
        helper: 'Mensagens ou acoes registradas',
        tone: 'positive',
        icon: 'i-lucide-send',
      },
      {
        id: 'blocked',
        label: 'Bloqueios/falhas',
        value: formatted(blockedCount + failedCount),
        helper: 'Ignorados, recusados ou com erro',
        tone: blockedCount + failedCount > 0 ? 'danger' : 'neutral',
        icon: 'i-lucide-octagon-alert',
      },
    ],
    sources: summarizeSources(items),
    timeline: buildTimeline(items),
    locks: buildExpectedLocks(activeKind),
    selectedItemId: items[0]?.id || '',
    empty: !loading && !items.length && !errorMessage,
  };

  const backend =
    operationSnapshot && typeof operationSnapshot === 'object'
      ? operationSnapshot
      : {};
  const backendArray = key => (Array.isArray(backend[key]) ? backend[key] : []);
  const preferArray = key =>
    backendArray(key).length ? backendArray(key) : fallback[key] || [];

  return {
    ...fallback,
    source: backend.source || source || fallback.source,
    generatedAt: backend.generatedAt || '',
    runPanel: backend.runPanel || fallback.runPanel,
    metrics: preferArray('metrics'),
    sources: preferArray('sources'),
    timeline: preferArray('timeline'),
    locks: preferArray('locks'),
    queues: backendArray('queues'),
    runs: backendArray('runs'),
    importGroups: backendArray('importGroups'),
    notificationGroups: backendArray('notificationGroups'),
    midas: backend.midas || {},
    readiness: backend.readiness || {
      status: fallback.empty ? 'warning' : 'unknown',
      checks: [],
      blockers: [],
      warnings: [],
      redacted: true,
    },
  };
};

export const pendingMarketOperationStatusTone = toneForStatus;
export const pendingMarketOperationReasonForItem = operationReasonForItem;
export const pendingMarketOperationPropertyTitle = propertyTitle;
